import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { clsx } from 'clsx'
import { Globe, Search, ChevronDown, PlayCircle, Check, Plus } from 'lucide-react'
import api from '../lib/api'

export interface PickerSite {
  id?: number | string
  domain: string
  name?: string
  description?: string
  favicon_url?: string
}

interface Props {
  sites: PickerSite[]
  value?: string
  onSelect: (domain: string) => void
  /** Volitelná akce „Spustit vše" u položky. */
  onRunAll?: (domain: string) => void
  placeholder?: string
  className?: string
}

const API_BASE = (api.defaults.baseURL as string) || '/tools/api/public/api/v1'
const shotUrl = (domain: string) =>
  `${API_BASE}/analyses/screenshot?url=${encodeURIComponent('https://' + domain.replace(/^https?:\/\//, ''))}&w=160&h=104`
// Favicon stažený z webu na náš server (ne přes Google — soukromí + spolehlivost)
const faviconUrl = (domain: string) =>
  `${API_BASE}/tools/favicon?domain=${encodeURIComponent(domain.replace(/^https?:\/\//, ''))}`

/** Hezký výběr webu — dropdown s vyhledáváním (autocomplete), screenshotem a SERP výpisem. */
export default function WebsitePicker({ sites, value, onSelect, onRunAll, placeholder, className }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [])

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50) }, [open])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return sites
    return sites.filter((s) =>
      s.domain.toLowerCase().includes(needle) ||
      (s.name || '').toLowerCase().includes(needle) ||
      (s.description || '').toLowerCase().includes(needle))
  }, [q, sites])

  const selected = sites.find((s) => s.domain === value)
  const typedIsUrl = q.trim().length > 2 && /\.[a-z]{2,}/i.test(q) && filtered.length === 0

  const pick = (domain: string) => { onSelect(domain); setOpen(false); setQ('') }

  return (
    <div ref={wrapRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-left hover:border-primary-300 transition"
      >
        {selected ? (
          <>
            <img src={faviconUrl(selected.domain)}
              alt="" className="w-5 h-5 rounded shrink-0" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
            <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-900 dark:text-white">{selected.name || selected.domain}</span>
          </>
        ) : (
          <>
            <Globe className="w-5 h-5 text-gray-400 shrink-0" />
            <span className="flex-1 text-sm text-gray-400">{placeholder || t('picker.placeholder')}</span>
          </>
        )}
        <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform shrink-0', open && 'rotate-180')} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.99 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6, scale: 0.99 }}
            transition={{ duration: 0.14 }}
            className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-200/80 dark:border-white/10 shadow-xl overflow-hidden"
          >
            {/* Hledání */}
            <div className="p-2 border-b border-gray-100 dark:border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && typedIsUrl) pick(q.trim()) }}
                  placeholder={t('picker.search')}
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                />
              </div>
            </div>

            {/* Výpis */}
            <div className="max-h-[22rem] overflow-y-auto p-1.5">
              {filtered.map((s) => (
                <div key={s.id ?? s.domain}
                  role="button"
                  tabIndex={0}
                  className="group flex items-stretch gap-3 px-2.5 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition cursor-pointer"
                  onClick={() => pick(s.domain)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(s.domain) } }}>
                  {/* Screenshot náhled (s favicon fallbackem) */}
                  <div className="relative w-[68px] h-[44px] shrink-0 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5">
                    <img src={shotUrl(s.domain)} alt="" loading="lazy"
                      className="w-full h-full object-cover object-top"
                      onError={(e) => { e.currentTarget.style.display = 'none' }} />
                    <img src={faviconUrl(s.domain)} alt=""
                      className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded bg-white shadow-sm"
                      onError={(e) => (e.currentTarget.style.display = 'none')} />
                  </div>
                  {/* SERP-style text */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">{s.name || s.domain}</p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 truncate">{s.domain}</p>
                    {s.description && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{s.description}</p>}
                  </div>
                  {/* Akce */}
                  <div className="flex items-center gap-1 shrink-0">
                    {value === s.domain && <Check className="w-4 h-4 text-primary-500" />}
                    {onRunAll && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onRunAll(s.domain); setOpen(false) }}
                        title={t('picker.run_all')}
                        className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-2 py-1.5 rounded-lg transition shrink-0"
                      >
                        <PlayCircle className="w-4 h-4" />
                        <span className="sm:hidden">{t('picker.run_all_short')}</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Prázdné / zadaná URL */}
              {filtered.length === 0 && (
                typedIsUrl ? (
                  <button onClick={() => pick(q.trim())}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-left">
                    <span className="w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-500/15 text-primary-600 flex items-center justify-center shrink-0"><Plus className="w-4 h-4" /></span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 dark:text-white">{t('picker.use_typed')}</span>
                      <span className="block text-xs text-emerald-600 truncate">{q.trim()}</span>
                    </span>
                  </button>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-6">{t('picker.nothing')}</p>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
